import {
	autoinject,
	bindable,
	bindingMode,
	containerless,
	customElement,
	observable,
} from 'aurelia-framework';

import {
	ContextMenuEvent,
	ContextMenuItem,
} from '~/components/context-menu-list/context-menu-list';

export interface ContextMenuPosition {
	x: number;
	y: number;
}

@autoinject
@containerless
@customElement('context-menu')
export class ContextMenu {
	@observable({ changeHandler: 'elementChanged' })
	private element: HTMLElement;

	/**
	 * The mouse position must be passed into this component
	 * for it to appear.
	 *
	 * The reason for this design choice was that we can't just query for the mouse
	 * position at anytime, we can only retrieve it in a click event, so the expected
	 * behaviour is that the parent component will store the mouse position after
	 * the click event and then pass the value into this component, making it appear visible and
	 * at the correct location.
	 */
	@bindable({
		defaultBindingMode: bindingMode.toView,
		changeHandler: 'mousePosChanged',
	})
	private readonly mousePos: ContextMenuPosition | undefined;

	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	private readonly items: ContextMenuItem[] = [];

	/**
	 * true when the context menu has not finished loading
	 */
	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	private isMenuLoading: boolean = false;

	/**
	 * Is the loader available for this context menu?
	 */
	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	private isLoaderEnabled: boolean = false;

	/**
	 * This is an optional attribute used to determine how many menu items to account for in the loader
	 */
	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	private loaderItemCount: number;

	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	private readonly onClose: (
		target: HTMLElement | undefined,
		event: ContextMenuEvent,
	) => void;

	private bind(bindingContext, overrideContext): void {
		if (!this.onClose) {
			throw new Error('Missing required onClose() callback.');
		}
	}

	private elementChanged(
		newElement: HTMLElement,
		oldElement: HTMLElement,
	): void {
		this.recalculatePosition();
	}

	private mousePosChanged(): void {
		this.recalculatePosition();
	}

	private recalculatePosition(): void {
		if (!this.element || !this.mousePos) {
			return;
		}
		const el = this.element;
		el.style.left = this.mousePos.x + 'px';
		el.style.top = this.mousePos.y + 'px';
		el.style.display = 'block';
	}

	private onCloseCallback = (
		target: HTMLElement | undefined,
		event: ContextMenuEvent,
	): void => {
		this.onClose(target, event);
	};

	private static hasButtonOrAnchorAncestor(element: HTMLElement): boolean {
		while (element) {
			if (element.tagName === 'BUTTON' || element.tagName === 'A') {
				return true;
			}
			element = element.parentElement;
		}
		return false;
	}
}
